/**
 * DashboardView Component
 *
 * New dashboard design showing:
 * - Greeting and doctor name
 * - Monthly overview statistics
 * - Today's appointments table with real-time data
 */

import React from 'react';
import {
  FileText,
  Search,
  Plus,
  FileCheck,
  ClockFading,
  ClipboardList,
  Mic,
  Pencil,
  Trash2,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Building2,
} from 'lucide-react';
import { RefreshButton } from '@/components/ui/refresh-button';
import { Button, Modal } from '../base';
import { Input } from '@/components/base/Form/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/base/Form/Select';
import { useAuthStore } from '@/lib/stores/authStore';
import { UserRoleBadge } from '@/components/shared/DynamicNav';
// notepad.png served from public/ as static asset (/notepad.png)
import { getAvatarColors } from '@/lib/utils/avatar';
import { formatLabel } from '@/lib/utils/format';
import { EditAppointmentModal } from '@/components/modals/EditAppointmentModal';
import {
  AppointmentRowData,
  formatAppointmentTime,
  getStatusDisplay,
  isDeleteLocked,
  summaryStatusLabels,
  APPOINTMENTS_TABLE_GRID_WITH_DOCTOR,
} from '@/components/shared/AppointmentsTable';
import type { FilterOption } from '@/components/shared/DataTable';
import type { OrganizationStatistics } from '@/lib/api/statistics';
import { AdminStatisticsSection } from './admin';

// Re-export for backwards compatibility
export type { AppointmentRowData } from '@/components/shared/AppointmentsTable';

export interface DashboardStatistics {
  totalConsultations: number;
  newConsultations: number;
  followUpVisits: number;
  isLoading: boolean;
  error?: string | null;
  selectedCard: 'all' | 'new' | 'followup';
  onCardSelect: (card: 'all' | 'new' | 'followup') => void;
}

export interface DashboardViewProps {
  appointments: {
    data: AppointmentRowData[];
    isLoading: boolean;
    error?: string | null;
  };
  statistics: DashboardStatistics;
  search: {
    value: string;
    onChange: (v: string) => void;
  };
  /** Doctor filter for column header */
  doctorFilter?: {
    value?: string;
    onChange: (v: string | undefined) => void;
    practitioners: FilterOption[];
  };
  /** Whether to show doctor column in the appointments table */
  showDoctorColumn?: boolean;
  actions: {
    onRecordClick?: () => void;
    onViewDetails?: (id: string) => void;
    onViewReport?: (id: string) => void;
    onStartConsultation?: (id: string) => void;
    onContinueConsultation?: (id: string, encounterId: string) => void;
    /** Navigate to the Case Sheet page to review a quick-submitted summary and save it */
    onVerify?: (encounterId: string) => void;
    onAddPatient?: () => void;
    onAddAppointment?: () => void;
    onEditAppointment?: (appointment: AppointmentRowData) => void;
    onDeleteAppointment?: (appointmentId: string) => Promise<void>;
    onAppointmentUpdated?: () => void;
  };
  isStartingConsultation?: string | null; // ID of appointment being opened
  isContinuingConsultation?: string | null; // ID of appointment being continued
  /** Show the consultation (mic) button - for roles that can start consultations */
  showConsultationButton?: boolean;
  /** Pagination */
  pagination?: {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
  };
  /** Callback to refresh the appointments data */
  onRefresh?: () => void;
  /** Admin organization statistics (shown for admin role) */
  adminStatistics?: OrganizationStatistics | null;
  isAdminStatsLoading?: boolean;
  adminStatsError?: string | null;
  onAdminStatsRefresh?: () => void;
  adminStartDate?: Date;
  adminEndDate?: Date;
  onAdminStartDateChange?: (date: Date) => void;
  onAdminEndDateChange?: (date: Date) => void;
}

export const DashboardView: React.FC<DashboardViewProps> = ({
  appointments,
  statistics,
  search,
  doctorFilter,
  showDoctorColumn = false,
  actions,
  isStartingConsultation,
  isContinuingConsultation,
  showConsultationButton = true,
  pagination,
  onRefresh,
  adminStatistics,
  isAdminStatsLoading,
  adminStatsError,
  onAdminStatsRefresh,
  adminStartDate,
  adminEndDate,
  onAdminStartDateChange,
  onAdminEndDateChange,
}) => {
  const user = useAuthStore((state) => state.user);
  const projectConfig = useAuthStore((state) => state.projectConfig);
  const visitTimeLabel = projectConfig?.projectBaseUrl?.includes('swecha')
    ? 'Arrival time'
    : 'Visit time';
  const doctorName = user
    ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username
    : 'Doctor';

  // Project name from auth state (multi-tenant)
  const projectName = user?.projectName || null;

  // Practitioner specialty (e.g. "Orthopedic Surgeon") from PractitionerRole metadata
  const practitionerSpecialty = user?.practitionerRole?.specialty || null;

  // Receptionist and Admin roles should not see the month/week overview statistics
  // Admin sees organization statistics instead. Also hide if projectConfig explicitly disables stats.
  const showStatistics =
    user?.role !== 'frontdesk' &&
    user?.role !== 'admin' &&
    projectConfig?.services.showStats !== false;

  // Modal state
  const [editModalOpen, setEditModalOpen] = React.useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [selectedAppointment, setSelectedAppointment] = React.useState<AppointmentRowData | null>(
    null
  );

  const handleEditAppointment = (appointment: AppointmentRowData) => {
    setSelectedAppointment(appointment);
    setEditModalOpen(true);
  };

  const handleDeleteAppointment = (appointment: AppointmentRowData) => {
    setSelectedAppointment(appointment);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedAppointment || !actions.onDeleteAppointment) return;

    setIsDeleting(true);
    try {
      await actions.onDeleteAppointment(selectedAppointment.id);
      setDeleteModalOpen(false);
      setSelectedAppointment(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAppointmentUpdated = () => {
    // Call parent callback to refetch appointments
    actions.onAppointmentUpdated?.();
    setEditModalOpen(false);
    setSelectedAppointment(null);
  };

  const appointmentsHeaderClassName =
    'font-hanken font-normal tracking-[-0.018px] text-[#061A61] text-[calc(20px*var(--app-scale))] leading-[calc(24px*var(--app-scale))]';

  const dashboardAppointmentsGrid =
    'grid-cols-[minmax(180px,2fr)_minmax(90px,1fr)_minmax(90px,1fr)_minmax(90px,1fr)_minmax(90px,1fr)_minmax(130px,1.5fr)_minmax(160px,2fr)]';

  const getStatusPillClassName = (
    status: string,
    originalStatus?: string,
    summaryStatus?: AppointmentRowData['summaryStatus']
  ) => {
    const baseClassName =
      'inline-flex min-w-[60px] items-center justify-center gap-[4px] rounded-full border px-2 py-0.5 font-hanken font-medium text-[calc(14px*var(--app-scale))]';

    // Quick-submit lifecycle takes priority over the FHIR-status-driven pill.
    if (summaryStatus === 'preparing') {
      return `${baseClassName} bg-[#f8f9fb] border-[#eaebf3] text-[#555661]`;
    }
    if (summaryStatus === 'pending_verification') {
      return `${baseClassName} bg-[#fff4e0] border-[#f0d8a8] text-[#7a5a0d]`;
    }

    const statusKey = (originalStatus || status || '').toLowerCase();

    if (statusKey === 'arrived') {
      return `${baseClassName} bg-[#E8F7EF] border-[#B6E6CC] text-[#1E9E62]`;
    }

    if (statusKey === 'booked') {
      return `${baseClassName} bg-[#EEF3FF] border-[#D1DBFD] text-[#6B8AF9]`;
    }

    return `${baseClassName} bg-[#f8f9fb] border-[#eaebf3] text-[#555661]`;
  };

  const statsCardClassName =
    'flex h-[calc(100px*var(--app-scale))] w-[calc(280px*var(--app-scale))] items-center gap-4 rounded-xl border-2 px-5 py-4 transition-all duration-200 cursor-pointer';
  const statsFieldTitleClassName =
    'font-hanken text-[calc(14px*var(--app-scale))] font-medium leading-5 tracking-[-0.1px] text-[#555661]';
  const statsFieldValueClassName =
    'font-hanken text-[calc(28px*var(--app-scale))] font-semibold leading-none tracking-[-0.3px] text-[#061A61]';

  return (
    <div className="relative flex w-full flex-col bg-[#f0f1f5]">
      <div className="flex flex-col py-6">
        {/* Greeting Section */}
        <div className="mb-2 flex flex-shrink-0 flex-col gap-0.5">
          {/* Project name badge - dynamic from auth state */}
          {projectName && (
            <div className="mb-2 inline-flex w-fit items-center gap-2 rounded-full bg-white px-4 py-1.5 shadow-sm">
              <Building2
                className="text-primary"
                style={{
                  width: 'calc(16px * var(--app-scale))',
                  height: 'calc(16px * var(--app-scale))',
                }}
              />
              <span className="font-hanken text-[calc(16px*var(--app-scale))] font-medium text-primary">
                {projectName}
              </span>
            </div>
          )}

          {/* Subtext - Figma: 20px / weight 500 / line-height 24px / letter-spacing 0px */}
          <p className="font-hanken text-[calc(20px*var(--app-scale))] font-medium text-gray-700">
            How are you doing today?
          </p>

          {/* Doctor name + specialty role badge */}
          <div className="flex items-center gap-3">
            <h1 className="font-serif text-[calc(20px*var(--app-scale))] font-medium text-pranik-brand-700">
              {doctorName}
            </h1>
            {practitionerSpecialty ? (
              <span className="inline-flex items-center rounded-full bg-primary-100 px-3 py-1 font-hanken text-[calc(16px*var(--app-scale))] font-semibold text-primary ring-1 ring-white/70">
                {practitionerSpecialty}
              </span>
            ) : (
              <UserRoleBadge />
            )}
          </div>
        </div>

        {/* Admin Organization Statistics - Only for admin role */}
        {user?.role === 'admin' && (
          <AdminStatisticsSection
            statistics={adminStatistics}
            isLoading={isAdminStatsLoading}
            error={adminStatsError}
            onRefresh={onAdminStatsRefresh}
            startDate={adminStartDate}
            endDate={adminEndDate}
            onStartDateChange={onAdminStartDateChange}
            onEndDateChange={onAdminEndDateChange}
          />
        )}

        {/* Overview Section - Hidden for front desk role */}
        {showStatistics && (
          <div className="mb-4 flex w-full flex-shrink-0 flex-col gap-4 sm:mb-6">
            {/* Statistics Cards */}
            <div className="flex flex-wrap gap-4">
              {/* Total Consultations */}
              <div
                className={`${statsCardClassName} ${
                  statistics.selectedCard === 'all'
                    ? 'border-[#061A61] bg-[#E9EDFE] shadow-md'
                    : 'border-[#D1DBFD] bg-white hover:border-[#6B8AF9] hover:shadow-sm'
                }`}
                onClick={() => statistics.onCardSelect('all')}
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#6B8AF9]/10">
                    <ClipboardList className="h-5 w-5 text-[#6B8AF9]" />
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <p className={statsFieldTitleClassName}>Total Consultations</p>
                    <p className={`${statsFieldValueClassName} text-[#061A61]`}>
                      {statistics.isLoading ? '...' : statistics.totalConsultations}
                    </p>
                  </div>
                </div>
              </div>

              {/* New Consultations */}
              <div
                className={`${statsCardClassName} ${
                  statistics.selectedCard === 'new'
                    ? 'border-[#061A61] bg-[#E9EDFE] shadow-md'
                    : 'border-[#D1DBFD] bg-white hover:border-[#6B8AF9] hover:shadow-sm'
                }`}
                onClick={() => statistics.onCardSelect('new')}
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#6B8AF9]/10">
                    <FileCheck className="h-5 w-5 text-[#6B8AF9]" />
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <p className={statsFieldTitleClassName}>New Consultations</p>
                    <p className={`${statsFieldValueClassName} text-[#061A61]`}>
                      {statistics.isLoading ? '...' : statistics.newConsultations}
                    </p>
                  </div>
                </div>
              </div>

              {/* Follow-up visits */}
              <div
                className={`${statsCardClassName} ${
                  statistics.selectedCard === 'followup'
                    ? 'border-[#061A61] bg-[#E9EDFE] shadow-md'
                    : 'border-[#D1DBFD] bg-white hover:border-[#6B8AF9] hover:shadow-sm'
                }`}
                onClick={() => statistics.onCardSelect('followup')}
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#6B8AF9]/10">
                    <ClockFading className="h-5 w-5 text-[#6B8AF9]" />
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <p className={statsFieldTitleClassName}>Follow-up Visits</p>
                    <p className={`${statsFieldValueClassName} text-[#061A61]`}>
                      {statistics.isLoading ? '...' : statistics.followUpVisits}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Appointments Section */}
        <div className="flex flex-col gap-2">
          <div className="flex flex-shrink-0 flex-wrap items-center justify-between gap-3">
            {/* Title */}
            <h2 className="flex-shrink-0 font-serif text-[calc(20px*var(--app-scale))] font-medium leading-[calc(24px*var(--app-scale))] tracking-[-0.15px] text-[#555661]">
              Today's Appointments
            </h2>
            {/* Search and Buttons - Right aligned */}
            <div className="flex flex-col items-end gap-2">
              {/* Buttons row */}
              <div className="flex flex-wrap items-center gap-3">
                <div className="relative w-full sm:w-[280px]">
                  <Input
                    type="text"
                    value={search.value}
                    onChange={(e) => search.onChange(e.target.value)}
                    placeholder="Search patient"
                    className="w-full rounded-full bg-white py-2 pl-10 pr-4 font-hanken text-[calc(14px*var(--app-scale))] font-medium text-gray-700 placeholder:text-[#bcc0d7]"
                  />
                  <Search
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-700"
                    style={{
                      width: 'calc(20px * var(--app-scale))',
                      height: 'calc(20px * var(--app-scale))',
                    }}
                  />
                </div>
                {/* Doctor Filter - legacy dropdown */}
                {doctorFilter && (
                  <Select
                    value={doctorFilter.value || 'all'}
                    onValueChange={(v) => doctorFilter.onChange(v === 'all' ? undefined : v)}
                  >
                    <SelectTrigger className="h-9 w-[200px] rounded-lg border-gray-200 bg-white font-hanken text-[calc(14px*var(--app-scale))] text-gray-700">
                      <SelectValue placeholder="All Doctors" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Doctors</SelectItem>
                      {doctorFilter.practitioners.map((practitioner) => (
                        <SelectItem key={practitioner.value} value={practitioner.value}>
                          {practitioner.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {actions.onAddPatient && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={actions.onAddPatient}
                    leftIcon={
                      <Plus
                        style={{
                          width: 'calc(16px * var(--app-scale))',
                          height: 'calc(16px * var(--app-scale))',
                        }}
                      />
                    }
                    className="h-9 rounded-lg border-[#061a61] px-3 text-[#061a61] hover:bg-[#061a61]/10"
                  >
                    <span className="font-hanken text-[calc(14px*var(--app-scale))] font-medium">
                      Add Patient
                    </span>
                  </Button>
                )}
                {actions.onAddAppointment && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={actions.onAddAppointment}
                    leftIcon={
                      <Plus
                        style={{
                          width: 'calc(16px * var(--app-scale))',
                          height: 'calc(16px * var(--app-scale))',
                        }}
                      />
                    }
                    className="h-9 rounded-lg bg-[#061a61] px-3 hover:bg-[#061a61]/90"
                  >
                    <span className="font-hanken text-[calc(16px*var(--app-scale))] font-medium">
                      Add Appointment
                    </span>
                  </Button>
                )}
              </div>
              {/* end buttons row */}
            </div>
            {/* end flex-col wrapper */}
          </div>

          {/* Appointments Table */}
          <div className="relative flex w-full flex-col overflow-x-auto rounded-3xl bg-white shadow-sm">
            {/* Refresh Button - positioned in top right corner of table */}
            {onRefresh && (
              <RefreshButton
                onClick={onRefresh}
                isLoading={appointments.isLoading}
                className="absolute right-4 top-3 z-10"
              />
            )}
            <div
              className={`flex flex-col ${showDoctorColumn ? 'min-w-[1120px]' : 'min-w-[960px]'}`}
            >
              {/* Table Header with inline filters */}
              <div
                className={`grid flex-shrink-0 ${showDoctorColumn ? APPOINTMENTS_TABLE_GRID_WITH_DOCTOR : dashboardAppointmentsGrid} items-center gap-[2px] border-b border-gray-200 px-3 py-1.5`}
              >
                <div className="text-left">
                  <p className={appointmentsHeaderClassName}>Patient name</p>
                  <p className="font-hanken text-[calc(14px*var(--app-scale))] font-normal leading-[calc(16px*var(--app-scale))] tracking-[-0.16px] text-[#555661]">
                    Registration number
                  </p>
                </div>
                <div className="text-center">
                  <p className={appointmentsHeaderClassName}>Age/Gender</p>
                </div>
                <div className="text-center">
                  <p className={appointmentsHeaderClassName}>Contact</p>
                </div>
                {showDoctorColumn && (
                  <div className="text-center">
                    <p className={appointmentsHeaderClassName}>Doctor</p>
                  </div>
                )}
                <div className="text-center">
                  <p className={appointmentsHeaderClassName}>Visit type</p>
                </div>
                <div className="text-center">
                  <p className={appointmentsHeaderClassName}>{visitTimeLabel}</p>
                </div>
                <div className="text-center">
                  <p className={appointmentsHeaderClassName}>Status</p>
                </div>
                <div className="text-center">
                  <p className={appointmentsHeaderClassName}>Action</p>
                </div>
              </div>

              {/* Table Body */}
              {appointments.isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <p className="font-hanken text-[calc(14px*var(--app-scale))] text-gray-600">
                    Loading appointments...
                  </p>
                </div>
              ) : appointments.error ? (
                <div className="flex items-center justify-center py-12">
                  <p className="font-hanken text-[calc(14px*var(--app-scale))] text-red-600">
                    {appointments.error}
                  </p>
                </div>
              ) : appointments.data.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-4">
                  <p className="mb-4 font-hanken text-[calc(14px*var(--app-scale))] font-medium text-gray-700">
                    {
                      {
                        new: 'No new patient appointments today',
                        followup: 'No follow-up appointments today',
                        all: 'No upcoming consultations yet',
                      }[statistics.selectedCard]
                    }
                  </p>
                  <img
                    src="/notepad.png"
                    alt="No consultations illustration"
                    className="mb-4 h-auto w-auto"
                    style={{ maxWidth: 'calc(200px * var(--app-scale))' }}
                  />
                  <div className="mb-6 text-center">
                    <p className="font-hanken text-[calc(13px*var(--app-scale))] text-gray-600">
                      {statistics.selectedCard === 'new'
                        ? 'All new patient appointments will appear here'
                        : statistics.selectedCard === 'followup'
                          ? 'All follow-up appointments will appear here'
                          : 'All your upcoming consultations and patient details'}
                    </p>
                    <p className="font-hanken text-[calc(13px*var(--app-scale))] text-gray-600">
                      {statistics.selectedCard === 'all' ? 'will appear here once scheduled' : ''}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {appointments.data.map((appointment) => (
                    <div
                      key={appointment.id}
                      className={`grid ${showDoctorColumn ? APPOINTMENTS_TABLE_GRID_WITH_DOCTOR : dashboardAppointmentsGrid} cursor-pointer items-center gap-[2px] px-3 py-2 hover:bg-gray-50`}
                      onClick={() => {
                        if (
                          isStartingConsultation === appointment.id ||
                          isContinuingConsultation === appointment.id
                        ) {
                          return;
                        }
                        if (!showConsultationButton) {
                          actions.onViewDetails?.(appointment.id);
                          return;
                        }
                        // Finished/closed visits open their details, as the mic button's
                        // neighbour does; an in-progress ('pending') visit with an encounter
                        // continues, exactly like the mic button below.
                        const status = appointment.originalStatus?.toLowerCase() ?? '';
                        if (['fulfilled', 'cancelled', 'noshow'].includes(status)) {
                          actions.onViewDetails?.(appointment.id);
                          return;
                        }
                        if (status === 'pending' && appointment.encounterId) {
                          actions.onContinueConsultation?.(appointment.id, appointment.encounterId);
                        } else {
                          actions.onStartConsultation?.(appointment.id);
                        }
                      }}
                    >
                      {/* Patient Info */}
                      <div className="flex items-center gap-3 pl-1">
                        {(() => {
                          const colors = getAvatarColors(appointment.patientName);
                          return (
                            <div
                              className="flex h-[calc(40px*var(--app-scale))] w-[calc(40px*var(--app-scale))] flex-shrink-0 items-center justify-center overflow-hidden rounded-xl border border-white/50"
                              style={{ backgroundColor: colors.bg }}
                            >
                              <span
                                className="font-hanken text-[calc(20px*var(--app-scale))] font-medium leading-[calc(24px*var(--app-scale))]"
                                style={{ color: colors.text }}
                              >
                                {appointment.patientName.charAt(0).toUpperCase()}
                              </span>
                            </div>
                          );
                        })()}
                        <div className="flex min-w-0 flex-col gap-0.5">
                          <p className="truncate font-hanken text-[calc(20px*var(--app-scale))] font-medium leading-[calc(24px*var(--app-scale))] tracking-[-0.18px] text-[#555661]">
                            {appointment.patientName}
                          </p>
                          <p className="truncate font-hanken text-[calc(14px*var(--app-scale))] font-normal leading-[calc(16px*var(--app-scale))] tracking-[-0.16px] text-[#555661]">
                            {formatLabel(projectConfig?.identifierLabel ?? 'ID')}:{' '}
                            {appointment.patientIdentifier || '-'}
                          </p>
                        </div>
                      </div>

                      {/* Gender/Age */}
                      <div className="text-center">
                        <p className="font-hanken text-[calc(20px*var(--app-scale))] font-normal leading-[calc(24px*var(--app-scale))] tracking-[-0.18px] text-[#555661]">
                          {appointment.age > 0 ? appointment.age : '-'} {appointment.gender}
                        </p>
                      </div>

                      {/* Contact */}
                      <div className="text-center">
                        <p className="font-hanken text-[calc(20px*var(--app-scale))] font-normal leading-[calc(24px*var(--app-scale))] tracking-[-0.18px] text-[#555661]">
                          {appointment.contact}
                        </p>
                      </div>

                      {/* Doctor Name - Visible when showDoctorColumn is true */}
                      {showDoctorColumn && (
                        <div className="text-center">
                          <p className="truncate font-hanken text-[calc(14px*var(--app-scale))] font-medium text-gray-700">
                            {appointment.doctorName || 'N/A'}
                          </p>
                        </div>
                      )}

                      {/* Visit Type */}
                      <div className="text-center">
                        <p className="truncate font-hanken text-[calc(20px*var(--app-scale))] font-normal leading-[calc(24px*var(--app-scale))] tracking-[-0.18px] text-[#555661]">
                          {appointment.visitType}
                        </p>
                      </div>

                      {/* Date & Time */}
                      <div className="text-center">
                        <p className="font-hanken text-[calc(20px*var(--app-scale))] font-normal leading-[calc(24px*var(--app-scale))] tracking-[-0.18px] text-[#555661]">
                          {formatAppointmentTime(appointment.appointmentStart)}
                        </p>
                      </div>

                      {/* Status */}
                      <div className="text-center">
                        <span
                          className={getStatusPillClassName(
                            appointment.status,
                            appointment.originalStatus,
                            appointment.summaryStatus
                          )}
                        >
                          {appointment.summaryStatus
                            ? summaryStatusLabels[appointment.summaryStatus]
                            : getStatusDisplay(appointment.status, appointment.originalStatus)}
                        </span>
                      </div>

                      {/* Actions */}
                      <div className="flex flex-shrink-0 items-center justify-end gap-2 pr-2">
                        {/* Verify - shown instead of View details/mic once this
                            encounter has been (quick-)submitted and is awaiting
                            doctor review. Disabled until the summary is ready. */}
                        {showConsultationButton && appointment.summaryStatus && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (appointment.summaryStatus === 'pending_verification') {
                                actions.onVerify?.(appointment.encounterId || appointment.id);
                              }
                            }}
                            disabled={appointment.summaryStatus === 'preparing'}
                            title={
                              appointment.summaryStatus === 'preparing'
                                ? 'Summary is still being prepared'
                                : 'Review the AI summary and save the record'
                            }
                            className={`flex flex-shrink-0 items-center justify-center whitespace-nowrap rounded-md px-3 font-hanken text-[calc(16px*var(--app-scale))] font-medium leading-[calc(20px*var(--app-scale))] tracking-[-0.18px] ${
                              appointment.summaryStatus === 'preparing'
                                ? 'cursor-not-allowed bg-[#f4f5f9] text-[#9aa6c4]'
                                : 'bg-[#14245c] text-white hover:bg-[#0a1a4a]'
                            }`}
                            style={{ height: 'calc(36px * var(--app-scale))' }}
                          >
                            Verify
                          </button>
                        )}

                        {/* View Details - only for clinical roles (not receptionist/staff) */}
                        {showConsultationButton && !appointment.summaryStatus && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (appointment.hasReport && actions.onViewReport) {
                                actions.onViewReport(appointment.id);
                              } else if (actions.onViewDetails) {
                                actions.onViewDetails(appointment.id);
                              }
                            }}
                            className="flex flex-shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-[#eaebf3] bg-[#eef6ff] px-2 hover:bg-[#dbeafe]"
                            style={{ height: 'calc(36px * var(--app-scale))' }}
                          >
                            <FileText
                              className="flex-shrink-0 text-[#566ec7]"
                              style={{
                                width: 'calc(20px * var(--app-scale))',
                                height: 'calc(20px * var(--app-scale))',
                              }}
                            />
                            <span className="font-hanken text-[calc(16px*var(--app-scale))] font-medium leading-[calc(20px*var(--app-scale))] tracking-[-0.18px] text-[#6b8af9]">
                              {appointment.hasReport ? 'View report' : 'View details'}
                            </span>
                          </button>
                        )}

                        {/* Mic Button - Start or continue consultation */}
                        {showConsultationButton && !appointment.summaryStatus && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (
                                appointment.originalStatus === 'pending' &&
                                appointment.encounterId
                              ) {
                                actions.onContinueConsultation?.(
                                  appointment.id,
                                  appointment.encounterId
                                );
                              } else {
                                actions.onStartConsultation?.(appointment.id);
                              }
                            }}
                            disabled={
                              isStartingConsultation === appointment.id ||
                              isContinuingConsultation === appointment.id
                            }
                            className={`flex h-9 w-9 items-center justify-center rounded-lg border border-[#eaebf3] ${
                              isStartingConsultation === appointment.id ||
                              isContinuingConsultation === appointment.id
                                ? 'cursor-not-allowed bg-gray-100 opacity-50'
                                : 'bg-[#eef6ff] hover:bg-[#dbeafe]'
                            }`}
                            title={
                              isStartingConsultation === appointment.id ||
                              isContinuingConsultation === appointment.id
                                ? 'Opening...'
                                : appointment.originalStatus === 'pending' &&
                                    appointment.encounterId
                                  ? 'Continue Consultation'
                                  : 'Start Consultation'
                            }
                          >
                            {isStartingConsultation === appointment.id ||
                            isContinuingConsultation === appointment.id ? (
                              <Loader2
                                className="animate-spin text-[#6b8af9]"
                                style={{
                                  width: 'calc(20px * var(--app-scale))',
                                  height: 'calc(20px * var(--app-scale))',
                                }}
                              />
                            ) : (
                              <Mic
                                className="text-[#6b8af9]"
                                style={{
                                  width: 'calc(20px * var(--app-scale))',
                                  height: 'calc(20px * var(--app-scale))',
                                }}
                              />
                            )}
                          </button>
                        )}

                        {/* Edit Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditAppointment(appointment);
                          }}
                          className="flex items-center justify-center rounded-md border border-[#eaebf3] bg-[#eef6ff] hover:bg-[#dbeafe]"
                          style={{
                            width: 'calc(36px * var(--app-scale))',
                            height: 'calc(36px * var(--app-scale))',
                          }}
                          title="Edit Appointment"
                        >
                          <Pencil
                            className="text-[#6b8af9]"
                            style={{
                              width: 'calc(20px * var(--app-scale))',
                              height: 'calc(20px * var(--app-scale))',
                            }}
                          />
                        </button>

                        {/* Delete Button — disabled once a consultation has been
                            started and put on hold (status "checked-in"), so an
                            in-progress patient record can't be lost by mistake. */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isDeleteLocked(appointment.originalStatus, appointment.encounterId))
                              return;
                            handleDeleteAppointment(appointment);
                          }}
                          disabled={isDeleteLocked(
                            appointment.originalStatus,
                            appointment.encounterId
                          )}
                          className={`flex items-center justify-center rounded-md border ${
                            isDeleteLocked(appointment.originalStatus, appointment.encounterId)
                              ? 'cursor-not-allowed border-[#eaebf3] bg-gray-100'
                              : 'border-[#eaebf3] bg-[#eef6ff] hover:bg-[#dbeafe]'
                          }`}
                          style={{
                            width: 'calc(36px * var(--app-scale))',
                            height: 'calc(36px * var(--app-scale))',
                          }}
                          title={
                            isDeleteLocked(appointment.originalStatus, appointment.encounterId)
                              ? 'Consultation is on hold — cannot delete until it is completed'
                              : 'Delete Appointment'
                          }
                        >
                          <Trash2
                            className={
                              isDeleteLocked(appointment.originalStatus, appointment.encounterId)
                                ? 'text-gray-400'
                                : 'text-[#6b8af9]'
                            }
                            style={{
                              width: 'calc(20px * var(--app-scale))',
                              height: 'calc(20px * var(--app-scale))',
                            }}
                          />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Pagination - Fixed at bottom right */}
          {pagination && pagination.totalPages > 1 && (
            <div className="fixed bottom-4 right-6">
              <div className="flex items-center gap-4 rounded-full bg-[#e6e8ef] px-4 py-2 shadow-md">
                <button
                  onClick={() => pagination.onPageChange(pagination.currentPage - 1)}
                  disabled={pagination.currentPage <= 1}
                  className="flex items-center justify-center disabled:opacity-50"
                  style={{
                    width: 'calc(24px * var(--app-scale))',
                    height: 'calc(24px * var(--app-scale))',
                  }}
                >
                  <ChevronLeft
                    className="text-[#42434b]"
                    style={{
                      width: 'calc(20px * var(--app-scale))',
                      height: 'calc(20px * var(--app-scale))',
                    }}
                  />
                </button>
                <span className="font-hanken text-[calc(14px*var(--app-scale))] font-medium text-[#42434b]">
                  {pagination.currentPage} out of {pagination.totalPages}
                </span>
                <button
                  onClick={() => pagination.onPageChange(pagination.currentPage + 1)}
                  disabled={pagination.currentPage >= pagination.totalPages}
                  className="flex items-center justify-center disabled:opacity-50"
                  style={{
                    width: 'calc(24px * var(--app-scale))',
                    height: 'calc(24px * var(--app-scale))',
                  }}
                >
                  <ChevronRight
                    className="text-[#42434b]"
                    style={{
                      width: 'calc(20px * var(--app-scale))',
                      height: 'calc(20px * var(--app-scale))',
                    }}
                  />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Edit Appointment Modal */}
        <EditAppointmentModal
          isOpen={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          appointment={selectedAppointment}
          onAppointmentUpdated={handleAppointmentUpdated}
        />

        {/* Delete Confirmation Modal */}
        <Modal
          open={deleteModalOpen}
          onOpenChange={(open) => !isDeleting && setDeleteModalOpen(open)}
          title="Delete Appointment"
          description="Are you sure you want to delete this appointment? This action cannot be undone."
          footer={
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setDeleteModalOpen(false)}
                disabled={isDeleting}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                disabled={isDeleting}
                loading={isDeleting}
                className="flex-1 bg-red-600 hover:bg-red-700"
                onClick={handleDeleteConfirm}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          }
        >
          <p className="text-[calc(14px*var(--app-scale))] text-gray-600">
            You are about to delete the appointment for{' '}
            <strong>{selectedAppointment?.patientName}</strong>.
          </p>
        </Modal>
      </div>
    </div>
  );
};

export default DashboardView;
